import React, { useEffect, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  AppBar,
  Body,
  ErrorCard,
  LoadingCard,
  LoadingPage,
  SuccessCard,
} from "../components";
import {
  useDownloadReceiptMutation,
  useQueryPaymentStatus,
  readPriceSearch,
  useQueryPrice,
  useQueryServiceByServiceId,
} from "../api/queries";
import { getFailureDisplay, getStorageToken, isErrorResponse } from "../utils";
import { useDimensions, useTheme } from "../hooks";
import { Alert, AlertTitle, Box, Button } from "@mui/material";
import { TRANSACTION_STATUS } from "../constants";
import { useTranslation } from "react-i18next";
import { getContrastTextColor } from "../utils/colorManager";
import { useGlobalContext } from "../context/globalContext";
import { usePortal } from "../context/portalContext";
import ContactSupport from "../components/ContactSupport";
import appConfig from "../config/config";
import { getSupportChannelConfig } from "../utils/getSupportChannelConfig";

const Transaction = () => {
  const { t, i18n } = useTranslation();
  const token = getStorageToken();
  const { isSmall } = useDimensions();
  const { id = "", transactionId = "" } = useParams();
  const [error, setError] = useState<string>("");
  const [reference, setReference] = useState<string>("");
  const navigate = useNavigate();

  const location = useLocation();
  const phoneNumber: string | undefined = location.state?.phoneNumber;
  const paddingX = isSmall ? 2 : "31%";
  const { pending, success, errored } = TRANSACTION_STATUS;
  const [status, setStatus] = useState<string>(pending);
  const downloadMutation = useDownloadReceiptMutation({
    authorisation: token!,
    itemId: transactionId,
  });

  const { currency } = useGlobalContext();

  const { theme } = useTheme();
  // The school and the branding come from the entry this visit resolved, not
  // from a page name read out of the address: a payment link has none there.
  // It also ends two lookups this page repeated on every mount.
  const { scope, state, homePath } = usePortal();

  const corporateId = scope?.entity_id ? String(scope.entity_id) : "";

  const service = useQueryServiceByServiceId({
    corporateId,
    serviceId: id,
    enabled: !!token && !!id && !!corporateId,
    authorisation: token || "",
  });

  const {
    data: paymentStatus,
    isLoading: isLoadingPayment,
    error: paymentError,
  } = useQueryPaymentStatus({
    paymentItemId: transactionId,
    enabled: !!token && !!transactionId,
    authorisation: token || "",
  });


  const [searchParams] = useSearchParams();
  const identifiers = readPriceSearch(searchParams);
  const recordedAmount = paymentStatus?.payment?.amount;
  const pricePayload =
    identifiers &&
      recordedAmount !== undefined &&
      Number.isFinite(Number(recordedAmount))
      ? { ...identifiers, amount: recordedAmount }
      : undefined;
  const { data: priceData, isInitialLoading: isLoadingPrice } = useQueryPrice({
    payload: pricePayload,
    enabled: !!token,
    authorisation: token || "",
  });
  const price = priceData && !isErrorResponse(priceData) ? priceData : undefined;
  const totalAmount = String(price?.total_amount ?? "");
  const totalFees = String(price?.total_fees ?? "");
  const instrumentFee = String(price?.instrument_fee ?? "");
  const registrationFee = String(price?.registration_fee ?? "");

  useEffect(() => {
    // A failed status read is not a failed payment. The payment has already
    // been accepted at this point, so keep the honest pending view visible
    // while the query retries/polls for an explicit final business status.
    if (paymentError) {
      setStatus(pending);
      setError("");
      setReference("");
      return;
    }

    if (!paymentStatus) return;

    const payment = paymentStatus.payment;
    const rawStatus = (payment?.status || payment?.data?.status || "")
      .toString()
      .toUpperCase();

    if (rawStatus === "SUCCESS" || rawStatus === "PAID") {
      setStatus(success);
      setError("");
      setReference(paymentStatus.reference);
    } else if (
      rawStatus === "FAILED" ||
      rawStatus === "ERROR" ||
      rawStatus === "DECLINED"
    ) {
      // `error` is the field that carries the provider's reason for the
      // failure. It arrives on a 200, so nothing on the seam's error path
      // ever sees it and the reference has to come from the envelope.
      const failure = getFailureDisplay(
        {
          body: payment?.error ?? payment?.data?.error,
          correlationId: paymentStatus.reference,
        },
        t,
        i18n.language
      );
      setStatus(errored);
      setError(failure.message);
      setReference(failure.reference || paymentStatus.reference);
    } else {
      setStatus(pending);
      setError("");
      setReference("");
    }
  }, [paymentStatus, paymentError, t, i18n.language, success, errored, pending]);

  // Derived at render -- React Query owns it. Kept apart from `reference`,
  // the browser-minted correlation id that names a failure, not the payment.
  const paymentReference = paymentError
    ? ""
    : paymentStatus?.payment?.payment_reference ?? "";

  // Only a lookup still in flight holds the page back. An entry that cannot
  // be resolved does not: what the payer paid outranks how the page is
  // branded, so the result is shown on the neutral theme rather than hidden.
  // `isInitialLoading`, because with no school the service read is never
  // sent, and a read that is never sent reports itself as loading for good.
  if (
    state === "resolving" ||
    service?.isInitialLoading ||
    isLoadingPayment ||
    isLoadingPrice
  )
    return <LoadingPage />;

  const isFinalResut = status === success || status === errored;

  return (
    <Body>
      <AppBar theme={theme} />
      {error && (
        <Box ml={1} mb={3}>
          <Alert
            severity={"error"}
            variant={"standard"}
            onClose={() => setError("")}
          >
            <AlertTitle sx={{ fontWeight: "bold" }}>
              {t("errors.title")}
            </AlertTitle>
            {error}
          </Alert>
        </Box>
      )}
      <Box>
        <Box
          sx={{
            display: "flex",
            height: "100%",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {/* {isFetching && <ProcessingCard />} */}
          {status === pending && (
            <LoadingCard
              totalAmount={totalAmount}
              currency={currency}
              phoneNumber={phoneNumber}
            />
          )}
          {status === success && (
            <SuccessCard
              totalAmount={totalAmount}
              currency={currency}
              totalFees={totalFees}
              instrumentFee={instrumentFee}
              registrationFee={registrationFee}
              paymentReference={paymentReference}
            />
          )}
          {status === errored && (
            <ErrorCard
              totalAmount={totalAmount}
              currency={currency}
              errorMessage={error}
              reference={reference}
              paymentReference={paymentReference}
            />
          )}
        </Box>
        {isFinalResut && (
          <Box
            sx={{
              alignItems: "center",
              paddingX,
            }}
          >
            <Button
              variant="contained"
              type="submit"
              fullWidth
              size="large"
              sx={{
                marginBottom: 2,
                bgcolor: theme.color_primary,
                color: getContrastTextColor(theme.color_primary),
              }}
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                navigate(homePath);
              }}
            >
              {t("stepper_4.make_other_payment")}
            </Button>

            {/* A failed payment produced no receipt to download. */}
            {status === success && (
              <Button
                variant="outlined"
                type="submit"
                fullWidth
                size="large"
                sx={{
                  marginBottom: 2,
                  // An outline needs a stroke colour, not a surface colour: the
                  // secondary fills panels behind text and can be near-white.
                  borderColor: theme.color_primary,
                  color: theme.color_primary,
                }}
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={() => {
                  downloadMutation.mutate();
                }}
                disabled={!paymentReference}
              >
                {t("button.download_reciept")}
              </Button>
            )}

            {/* The reporting route, in the same stack and the same treatment as
                the buttons above it. It replaces a third button that opened a
                hardcoded service desk -- one screen, one place to report. */}
            <ContactSupport
              signals={{ stage: status === errored ? "pay" : "receipt" }}
              facts={{
                reference,
                paymentReference,
                amount: totalAmount,
                route: "/transaction",
              }}
              config={getSupportChannelConfig(appConfig)}
              tone="brand"
              showPrompt={false}
            />
          </Box>
        )}
      </Box>
    </Body>
  );
};

export default Transaction;
